import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from "@angular/core";
import { Store } from "../../store";
import { UserService } from "../../services/user.service";
import { ToastrService } from "ngx-toastr";
import { AlertService } from "../../services/alert.service";
import { AddressData, AddressType, DefaultAddressData } from "../../data-types/user-data";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { SettingsService } from "../../services/settings.service";
import { CustomAlertData, DefaultCustomAlertData } from "../../data-types/alert-data";
import { debounce } from "lodash";
import { first } from "rxjs/operators";
import { Observable } from "rxjs";

@Component({
    selector: 'app-address-form',
    templateUrl: './address-form.component.html',
    styleUrls: ['./address-form.component.scss']
})
export class AddressFormComponent implements OnInit {
    @Input() customerId = '';
    @Input() showCurrentAddresses = false;
    @Input() type: AddressType = AddressType.Billing;
    @Input() defaultDeliveryAddress = true;
    @Input() initialAddress: AddressData = null;
    @Input() mode: "edit" | "new" = 'new';
    @Input() style: "modal" | "inline" = 'inline';
    @Input() makeDirty: Observable<boolean> = null;


    @Input() set modalShowHide(value: boolean) {
        if (value === true) {
            this.showAddressModal();
            this.initDefaultAddress();
        }
    }
    @Input() title: string = null;
    @Input() requiredFields = {
        adr1: false,
        adr2: false,
        adr3: false,
        town: false,
        county: false,
        postcode: false,
        country: false
    };

    form: FormGroup;
    address: AddressData = { ...DefaultAddressData };
    manualAddress = false;
    countries: Array<any> = [];

    @Output() validAddress = new EventEmitter(); // this is fired when user filles out all required fields
    @Output() addressAdded = new EventEmitter(); // this is fire on modal mode when user confirms valid address
    @Output() existingAddressSelected = new EventEmitter();
    @Output() cancelled = new EventEmitter();
    @Output() modalShowHideChange = new EventEmitter();
    addressResults = [];
    selectedUkAddress: AddressData;

    @ViewChild('addressTmpl', { read: TemplateRef }) addressTmpl: TemplateRef<any>;
    user = this.store.selectForLocal('user');
    addresses = [];
    selectedDeliveryAddressId = '';

    private haveSelectedAddress = false;

    constructor(
        private store: Store,
        private userService: UserService,
        private settingsService: SettingsService,
        private toastrService: ToastrService,
        private cd: ChangeDetectorRef,
        private alertService: AlertService,
        private fb: FormBuilder
    ) {
        this.settingsService.getCountries().subscribe(result => {
            result.sort((item1, item2) => {
                return item1.sortOrder - item2.sortOrder;
            });
            this.countries = result;
        });
        this.initDefaultAddress = debounce(this.initDefaultAddress, 70);
        this.showAddressModal = debounce(this.showAddressModal, 50);
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            adr1: ['', [Validators.minLength(2)].concat(this.requiredFields.adr1 ? [Validators.required] : [])],
            adr2: ['', [Validators.minLength(2)].concat(this.requiredFields.adr2 ? [Validators.required] : [])],
            adr3: ['', [Validators.minLength(2)].concat(this.requiredFields.adr3 ? [Validators.required] : [])],
            town: ['', [Validators.minLength(2)].concat(this.requiredFields.town ? [Validators.required] : [])],
            county: ['', [Validators.minLength(2)].concat(this.requiredFields.county ? [Validators.required] : [])],
            postcode: ['', [Validators.minLength(2)].concat(this.requiredFields.postcode ? [Validators.required] : [])],
            country: ['GB', [Validators.minLength(2), Validators.required]],
        });

        this.form.valueChanges.subscribe(values => {
            if (this.form.valid) {
                this.validAddress.emit({
                    ...this.address, ...{
                        ...values,
                        add1: values.adr1,
                        add2: values.adr2,
                        add3: values.adr3,
                    }
                });
            } else {
                this.validAddress.emit(null);
            }
        });

        if (this.makeDirty) {
            this.makeDirty.subscribe(value => value === true && this.form.markAllAsTouched());
        }

        this.initDefaultAddress();
    }

    showAddressModal() {
        if (this.style === 'modal') {
            this.alertService
                .customAlert({
                    ...DefaultCustomAlertData,
                    template: this.addressTmpl,
                    cancelText: "Cancel",
                    confirmText: this.mode === 'new' ? "Add" : 'Update',
                    confirmCb: this.fireAddressAction.bind(this),
                    canConfirmCb: this.canConfirm.bind(this),
                    cancelCb: this.fireCancelAddAddress.bind(this),
                    confirmClass: 'btn-styled',
                    styleClass: 'search-address-form'
                } as CustomAlertData
                );
        }
    }

    private initDefaultAddress() {
        if (this.mode === 'new') {
            this.address = { ...DefaultAddressData };
            this.address.defaultDeliveryAddress = this.defaultDeliveryAddress;
        }

        this.manualAddress = false;
        this.addressResults = [];

        if (this.initialAddress) { // need a copy of a given address to avoid null loops
            this.address = { ...this.initialAddress };
            if (this.address.country !== "GB") {
                this.manualAddress = true;
            }
        }

        if (this.showCurrentAddresses && this.customerId) {
            this.getExistingAddresses();
        }

        if (this.type !== AddressType.Billing) {
            this.address.isBilling = false;
        }

        this.assignAddress2Form();
    }

    private canConfirm(): boolean {
        return this.form.valid || this.haveSelectedAddress;
    }

    private fireAddressAction() {
        this.existingAddressSelected.pipe(first()).toPromise();
        this.addressAdded.emit({
            ...this.form.value,
            add1: this.form.value.adr1,
            add2: this.form.value.adr2,
            add3: this.form.value.adr3,
        });
        this.modalShowHideChange.emit(false);
    }

    private fireCancelAddAddress() {
        this.cancelled.emit([this.form.valid, this.address]);
        this.modalShowHideChange.emit(false);
    }

    private assignAddress2Form() {
        this.form.patchValue({
            adr1: this.address.add1 ? this.address.add1 : '',
            adr2: this.address.add2 ? this.address.add2 : '',
            adr3: this.address.add3 ? this.address.add3 : '',
            town: this.address.town ? this.address.town : '',
            postcode: this.address.postcode ? this.address.postcode : '',
            county: this.address.county ? this.address.county : '',
            country: this.address.country ? this.address.country : ''
        });
    }

    manualAddressToggle() {
        this.manualAddress = !this.manualAddress;
        if (this.manualAddress === false) {
            this.address.country = "GB";
            this.form.patchValue({
                postcode: this.mode === 'edit' ? this.address.postcode : '',
                country: this.address.country
            });
        }
    }

    getExistingAddresses() {
        this.userService.findAddressesByCustomer(this.customerId).subscribe((data: any) => {
            this.addresses = data.data;
        });
    }

    selectExistingAddress(adr) {
        this.existingAddressSelected.emit(adr);
        this.haveSelectedAddress = true;
    }

    async findAddresses() {

        this.addressResults = [];
        try {
            const addressData = await this.userService.postcodeLookup(this.address.postcode);
            this.addressResults = addressData.addresses;
            this.address.postcode = addressData.postcode;


        } catch (err) {
            if ((err.status / 100) % 10 !== 4) { // if an error is not from 4xx range
                throw new Error(JSON.stringify(err));
            }
            this.alertService.error(['Sorry - we could not find an address for the postcode you entered']);
        }
    }

    public handleCountryChange(country): void {
        this.address.country = country;
        this.form.patchValue({
            country: this.address.country
        });
    }

    public handleAddressChange(adr): void {
        this.selectedUkAddress = adr;
        if (adr !== -1) {
            this.address.add1 = adr.line_1;
            this.address.add2 = adr.line_2;
            this.address.add3 = adr.line_3;
            this.address.town = adr.locality;
            if (!adr.locality) {
                this.address.town = adr.town_or_city;
            }
            this.address.county = adr.county;
            this.assignAddress2Form();
        } else {
            this.initDefaultAddress();
        }
    }

    get addressType(): typeof AddressType {
        return AddressType;
    }
}
